import i18n from 'i18next';

const ee = window.ee; // @TODO Move on to @google/earthengine

export { ee };

export const authenticateViaOauth = async (
  clientId,
  scopes = null,
  onImmediateFailure = () => {}
) => {
  return await new Promise((resolve, reject) => {
    ee.data.authenticateViaOauth(
      clientId,
      (success) => resolve(success),
      (error) => reject(error),
      scopes,
      (immediate) => {
        try {
          onImmediateFailure();
          resolve();
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

export const load = async (baseUrl = null, tileUrl = null) => {
  window.ee = ee;
  return await new Promise((resolve, reject) => {
    ee.initialize(
      baseUrl,
      tileUrl,
      (success) => resolve(success),
      (error) => reject(error)
    );
  });
};

export const initializeEEConnection = async (
  clientId = null,
  scopes = null,
  onImmediateFailure = () => {}
) => {
  await authenticateViaOauth(clientId, scopes, onImmediateFailure);
  await load();
  return ee.data.getAuthToken();
};

let initializationPromise = null;

/**
 * @function isEarthEngineInitialized
 * @description Reports whether `ee.initialize()` has already completed. The Earth Engine client
 * builds most of its namespaces dynamically from the algorithm list downloaded during
 * `initialize()` — `ee.Reducer` is one of them and does not exist as a property of `ee` before that
 * round trip finishes (the string "Reducer" appears nowhere in `ee_api_js.js`), unlike statically
 * defined namespaces such as `ee.Image`, `ee.Filter` or `ee.Geometry`. So the presence of
 * `ee.Reducer` is the cheapest reliable probe for "the generated API is available".
 * **Called by:
 * @see {ensureEarthEngineReady}
 * @memberof module:SHARED
 * @returns {Boolean} True once the generated Earth Engine namespaces exist.
 */
const isEarthEngineInitialized = () => Boolean(ee.Reducer);

/**
 * @function ensureEarthEngineReady
 * @description Guards flows (like a module's `importSessionState`) that call `ee.*` directly
 * without going through the normal wizard. Two distinct preconditions have to hold, and only the
 * first one used to be checked:
 * 1. An auth token must be set, otherwise Earth Engine fails with a raw "Request is missing
 *    required authentication credential" stack trace straight from `ee_api_js.js`.
 * 2. `ee.initialize()` must have *completed*. The auth saga sets the token and then fires
 *    `ee.initialize()` without awaiting it, so there is a window where `ee.data.getAuthToken()`
 *    already returns a token while the generated namespaces are still missing. Replaying a session
 *    in that window breaks on the first generated namespace it touches — for bathymetry that is
 *    `ee.Reducer.sum()` inside geeSharp's `brovey`, reached through `generateCollection` ->
 *    `sharpenCollection`, surfacing as "Cannot read properties of undefined (reading 'sum')".
 * When the token is present but initialization has not finished, this awaits it (reusing a single
 * in-flight promise so concurrent importers don't trigger parallel `initialize()` calls) instead of
 * failing, since a session import is a long user-driven action that can simply wait.
 * Reads the module-level `i18n` singleton directly (rather than requiring callers to thread a `t`
 * through) since `importSessionState` is plain module code, not a React component/hook.
 * **Called by:
 * @see {importSessionState} - bathymetry, csqueeze, and euscoast, before replaying any `ee.*` calls.
 * @memberof module:SHARED
 * @returns {Promise<void>}
 * @throws {Error} With `code === 'EE_NOT_AUTHENTICATED'` when no token is set, or when
 *  initialization fails.
 */
export const ensureEarthEngineReady = async () => {
  if (!ee.data.getAuthToken()) {
    const error = new Error(i18n.t('forms.acquisition.sessionImport.notAuthenticated'));
    error.code = 'EE_NOT_AUTHENTICATED';
    throw error;
  }

  if (isEarthEngineInitialized()) return;

  if (!initializationPromise) {
    initializationPromise = load().catch((e) => {
      initializationPromise = null;
      throw e;
    });
  }

  try {
    await initializationPromise;
  } catch (e) {
    const error = new Error(i18n.t('forms.acquisition.sessionImport.notAuthenticated'));
    error.code = 'EE_NOT_AUTHENTICATED';
    error.cause = e;
    throw error;
  }
};

export default ee;
